/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from 'react';
import Image from 'react-bootstrap/Image';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { IMAGES } from '../../../../constants/imagePath';
import { StepLocationFormValues, StepProps } from '../../../../redux/modules/auth/types';
import { setStepLocationData } from '../../../../redux/modules/auth/accountSetupSlice';
import { RootState } from '../../../../redux/rootReducer';
import useLocationData from '../../../../hooks/useLocationData';
import XSelect from '../../../../component/form/XSelect';
import XInput from '../../../../component/form/XInput';
import PhoneNumberInput from '../../../../component/phoneInput/phoneInput';
import Loader from '../../../../component/loader';
import { timezoneOptions } from '../../../../utils/options';
import Stepper from './Stepper';

const MAX_LOGO_SIZE_BYTES = 1024 * 1024;

const StepLocationValidationSchema = Yup.object().shape({
  display_name: Yup.string().trim().required('Location Name is required'),
  address: Yup.string().trim().required('Address is required'),
  city: Yup.string().trim().required('City is required'),
  country_id: Yup.string().required('Country is required'),
  state_id: Yup.string().required('State is required'),
  zipcode: Yup.string()
    .matches(/^\d{5}$/, 'Zipcode must be a number of 5 digits')
    .required('Zipcode is required'),
  reply_to_email: Yup.string().trim().email('Enter a valid email address').required('Reply-to Email is required'),
  from_name: Yup.string().trim().required('From Email Name is required'),
  location_timezone: Yup.string().required('Timezone is required'),
  phone: Yup.string()
    .test('valid-phone', 'Please enter a valid phone number', (val) => {
      if (!val) return true;
      const digits = val.replace(/\D/g, '');
      return digits.length >= 7 && digits.length <= 15;
    }),
});

const StepLocation: React.FC<StepProps> = ({ onNext, onBack }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewLogo, setPreviewLogo] = useState<string | null>(null);
  const [sameAsBusiness, setSameAsBusiness] = useState<boolean>(false);
  const [showConfirmModal, setShowConfirmModal] = useState<boolean>(false);

  const stepOne = useSelector((state: RootState) => state.accountSetup.stepOne);
  const stepTwo = useSelector((state: RootState) => state.accountSetup.stepTwo);
  const stepThree = useSelector((state: RootState) => state.accountSetup.stepThree);
  const saved = useSelector((state: RootState) => state.accountSetup.stepLocationData);
  const { loading, error, data } = useSelector((state: RootState) => state.accountSetup);
  const { countries, states, fetchStates } = useLocationData();

  useEffect(() => {
    if (data && data.success === true) {
      setShowConfirmModal(false);
      toast.success(data.message || 'Registration completed successfully.');
      navigate('/login');
    } else if (error && error.success === false) {
      toast.error(error.message || 'Something went wrong.');
    }
  }, [error, data]);

  const formik = useFormik<StepLocationFormValues>({
    initialValues: {
      display_name: saved?.display_name ?? '',
      phone: saved?.phone ?? '',
      address: saved?.address ?? '',
      city: saved?.city ?? '',
      country_id: saved?.country_id ?? '',
      state_id: saved?.state_id ?? '',
      zipcode: saved?.zipcode ?? '',
      reply_to_email: saved?.reply_to_email ?? '',
      from_name: saved?.from_name ?? '',
      location_timezone: saved?.location_timezone || 'UTC',
      logo: saved?.logo ?? null,
    },
    validationSchema: StepLocationValidationSchema,
    onSubmit: () => {
      // Open verification modal when form validation passes
      setShowConfirmModal(true);
    },
  });

  const handleSameAsBusinessChange = (checked: boolean) => {
    setSameAsBusiness(checked);
    if (checked) {
      if (stepTwo?.country) {
        fetchStates(stepTwo.country);
      }
      formik.setFieldValue('address', stepTwo?.address ?? '');
      formik.setFieldValue('country_id', stepTwo?.country ?? '');
      formik.setFieldValue('state_id', stepTwo?.state ?? '');
      formik.setFieldValue('city', stepTwo?.city ?? '');
      formik.setFieldValue('zipcode', stepTwo?.zipCode ?? '');
    } else {
      formik.setFieldValue('address', '');
      formik.setFieldValue('country_id', '');
      formik.setFieldValue('state_id', '');
      formik.setFieldValue('city', '');
      formik.setFieldValue('zipcode', '');
    }
  };

  const handleConfirmSubmit = () => {
    dispatch(setStepLocationData(formik.values));
    if (onNext) {
      onNext();
    }
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_LOGO_SIZE_BYTES) {
      toast.error('Location logo exceeds 1 MB. Please choose a location logo no larger than 1 MB.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast.error('File is not an image. Please upload an image file.');
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setPreviewLogo(objectUrl);
    formik.setFieldValue('logo', file);
  };

  const handleRemoveLogo = () => {
    setPreviewLogo(null);
    formik.setFieldValue('logo', null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const getCountryName = (countryId: string | number | undefined) => {
    if (!countryId) return '';
    return (countries as any)?.[countryId]?.text || String(countryId);
  };

  const getStateName = (stateId: string | number | undefined) => {
    if (!stateId) return '';
    return (states as any)?.[stateId]?.text || String(stateId);
  };

  useEffect(() => {
    if (formik.values.logo instanceof File && !previewLogo) {
      setPreviewLogo(URL.createObjectURL(formik.values.logo));
    } else if (!formik.values.logo && previewLogo) {
      setPreviewLogo(null);
    }
  }, [formik.values.logo]);

  useEffect(() => {
    if (formik.values.country_id) {
      fetchStates(formik.values.country_id);
    }
  }, [formik.values.country_id]);

  const errorList = Object.values(formik.errors).filter(Boolean) as string[];
  const showErrorBox = formik.submitCount > 0 && errorList.length > 0;

  return (
    <div>
      {/* // -------------------- 4 step (location) -------------------------- */}
      <div className="signup-process">
        <Container>
          <div className="content">
            <Image src={IMAGES.blueLogo} className="top-logo" />
            <div className="card">
              <h2 className="card-title">Account Setup</h2>
              <Row className="justify-content-center">
                <Col xl={10}>
                  <Stepper currentStep={4} />

                  {showErrorBox && (
                    <div className="error-box" style={{ display: 'block' }}>
                      <span style={{ fontWeight: 'bold' }}>
                        {`There are ${errorList.length} errors in your form.`}<br />
                      </span>
                      {errorList.map((error, index) => (
                        <span key={index}>{error}<br /></span>
                      ))}
                    </div>
                  )}

                  <Form onSubmit={formik.handleSubmit}>
                    <div className="form-title">
                      <h2>Set up your first location</h2>
                      <p>Enter your organization's primary location details. You can add more locations later from your settings.</p>
                    </div>

                    <Row>
                      {/* Location Name & Location Logo */}
                      <Col xl={6} lg={6} md={6} sm={12}>
                        <XInput
                          name="display_name"
                          label="Location Name"
                          placeholder="e.g. Nashville"
                          formik={formik}
                        />
                      </Col>

                      <Col xl={6} lg={6} md={6} sm={12}>
                        <Form.Group className="form-group position-relative">
                          <Form.Label>Location Logo</Form.Label>
                          <div className="position-relative">
                            <Form.Control
                              ref={fileInputRef}
                              type="file"
                              id="location-logo-file"
                              accept="image/*"
                              className="visually-hidden"
                              onChange={handleLogoChange}
                            />
                            <label htmlFor="location-logo-file" className="logo-dropzone">
                              <svg className="logo-dropzone-icon" viewBox="0 0 24 24" aria-hidden="true">
                                <path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14.5v3A2.5 2.5 0 0 0 7.5 20h9a2.5 2.5 0 0 0 2.5-2.5v-3" />
                              </svg>
                              <span className="logo-dropzone-action">
                                {previewLogo ? 'Edit Logo' : 'Upload logo'}
                              </span>
                              {previewLogo && (
                                <img
                                  className="logo-dropzone-preview"
                                  alt="Location logo preview"
                                  src={previewLogo}
                                />
                              )}
                            </label>
                            {previewLogo && (
                              <button
                                type="button"
                                className="logo-dropzone-remove"
                                aria-label="Remove location logo"
                                title="Remove Logo"
                                onClick={handleRemoveLogo}
                              >
                                <FontAwesomeIcon icon={faCircleXmark} />
                              </button>
                            )}
                          </div>
                        </Form.Group>
                      </Col>

                      {/* Same as business address Checkbox */}
                      <Col xl={12}>
                        <div className="same-as-business-wrapper mb-3">
                          <Form.Check
                            type="checkbox"
                            id="sameAsBusinessCheckbox"
                            className="custom-checkbox-align"
                            label="Same as business address"
                            checked={sameAsBusiness}
                            onChange={(e) => handleSameAsBusinessChange(e.target.checked)}
                          />
                        </div>
                      </Col>

                      {/* Address Fields (Symmetrical 2-row layout matching Step 2) */}
                      <Col xl={6} lg={6} md={6} sm={12}>
                        <XInput
                          name="address"
                          label="Address"
                          placeholder="Enter"
                          formik={formik}
                          disabled={sameAsBusiness}
                        />
                      </Col>
                      <Col xl={6} lg={6} md={6} sm={12}>
                        <XSelect
                          name="country_id"
                          label="Country"
                          options={Object.values(countries)}
                          formik={formik}
                          disabled={sameAsBusiness}
                          onChange={(e: any) => {
                            formik.handleChange(e);
                            formik.setFieldValue('state_id', '');
                          }}
                        />
                      </Col>
                      <Col xl={4} lg={4} md={6} sm={12}>
                        <XSelect
                          name="state_id"
                          label="State"
                          options={Object.values(states)}
                          formik={formik}
                          disabled={sameAsBusiness || !formik.values.country_id}
                        />
                      </Col>
                      <Col xl={4} lg={4} md={6} sm={12}>
                        <XInput
                          name="city"
                          label="City"
                          placeholder="Enter"
                          formik={formik}
                          disabled={sameAsBusiness}
                        />
                      </Col>
                      <Col xl={4} lg={4} md={6} sm={12}>
                        <XInput
                          name="zipcode"
                          label="ZIP Code"
                          placeholder="Enter"
                          formik={formik}
                          disabled={sameAsBusiness}
                        />
                      </Col>

                      {/* Phone & Timezone */}
                      <Col xl={6} lg={6} md={6} sm={12}>
                        <PhoneNumberInput
                          value={formik.values.phone}
                          onChange={(phone) => formik.setFieldValue('phone', phone)}
                          error={formik.errors.phone}
                          touched={formik.touched.phone}
                          label="Phone Number"
                          placeholder="Enter phone number"
                          country="us"
                        />
                      </Col>
                      <Col xl={6} lg={6} md={6} sm={12}>
                        <XSelect
                          name="location_timezone"
                          label="Location Timezone"
                          options={timezoneOptions}
                          formik={formik}
                        />
                      </Col>

                      {/* Reply-to Email & From Email Name */}
                      <Col xl={6} lg={6} md={6} sm={12}>
                        <XInput
                          name="reply_to_email"
                          label="Reply-to Email"
                          placeholder="contact@your-domain.com"
                          formik={formik}
                        />
                      </Col>
                      <Col xl={6} lg={6} md={6} sm={12}>
                        <XInput
                          name="from_name"
                          label="From Email Name"
                          placeholder="Your Brand Name"
                          formik={formik}
                        />
                      </Col>
                    </Row>

                    <div className="d-flex justify-content-between align-items-center">
                      <Button
                        variant="primary"
                        type="button"
                        className="btn-theme btn-border"
                        onClick={onBack}
                        disabled={loading}
                      >
                        <FontAwesomeIcon icon={faPlay} />Back
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        className="btn-theme"
                        disabled={loading}
                      >
                        {loading && <Loader />}
                        Submit<FontAwesomeIcon icon={faPlay} />
                      </Button>
                    </div>
                  </Form>
                </Col>
              </Row>
            </div>
          </div>
        </Container>
      </div>

      {/* Confirmation Modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => !loading && setShowConfirmModal(false)}
        centered
        className="account-confirm-modal"
      >
        <Modal.Header closeButton={!loading} className="align-items-start">
          <div className="pe-3">
            <Modal.Title className="mb-2">
              Verify & Confirm Details
            </Modal.Title>
            <p className="confirm-intro-text mb-0">
              Please review and verify that all your organization and location details are correctly filled before finalizing your account setup.
            </p>
          </div>
        </Modal.Header>
        <Modal.Body>
          {/* Organization Details Card */}
          <div className="confirm-card">
            <h5 className="confirm-card-title">Organization Details</h5>
            <Row className="g-3">
              <Col md={6} xs={12}>
                <div className="confirm-field-item">
                  <div className="confirm-field-label">Contact Name</div>
                  <div className="confirm-field-value">{stepOne?.firstName} {stepOne?.lastName}</div>
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="confirm-field-item">
                  <div className="confirm-field-label">Business Name</div>
                  <div className="confirm-field-value">{stepOne?.businessName || '-'}</div>
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="confirm-field-item">
                  <div className="confirm-field-label">Phone Number</div>
                  <div className="confirm-field-value">{stepOne?.phoneNumber || '-'}</div>
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="confirm-field-item">
                  <div className="confirm-field-label">Website URL</div>
                  <div className="confirm-field-value">{stepThree?.websiteURL || 'N/A'}</div>
                </div>
              </Col>
              <Col xs={12}>
                <div className="confirm-field-item mb-0">
                  <div className="confirm-field-label">Business Address</div>
                  <div className="confirm-field-value">
                    {[
                      stepTwo?.address,
                      stepTwo?.city,
                      getStateName(stepTwo?.state),
                      getCountryName(stepTwo?.country),
                      stepTwo?.zipCode,
                    ].filter(Boolean).join(', ') || '-'}
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          {/* Location Details Card */}
          <div className="confirm-card">
            <h5 className="confirm-card-title">Location Details</h5>
            <Row className="g-3">
              <Col md={6} xs={12}>
                <div className="confirm-field-item">
                  <div className="confirm-field-label">Location Name</div>
                  <div className="confirm-field-value">{formik.values.display_name || '-'}</div>
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="confirm-field-item">
                  <div className="confirm-field-label">Location Phone</div>
                  <div className="confirm-field-value">{formik.values.phone || '-'}</div>
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="confirm-field-item">
                  <div className="confirm-field-label">Reply-to Email</div>
                  <div className="confirm-field-value">{formik.values.reply_to_email || '-'}</div>
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="confirm-field-item">
                  <div className="confirm-field-label">From Email Name</div>
                  <div className="confirm-field-value">{formik.values.from_name || '-'}</div>
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="confirm-field-item">
                  <div className="confirm-field-label">Timezone</div>
                  <div className="confirm-field-value">{formik.values.location_timezone || 'UTC'}</div>
                </div>
              </Col>
              <Col md={6} xs={12}>
                <div className="confirm-field-item">
                  <div className="confirm-field-label">Location Logo</div>
                  <div className="confirm-field-value">
                    {previewLogo ? (
                      <div className="confirm-logo-preview-box">
                        <img
                          src={previewLogo}
                          alt="Location Logo Preview"
                          className="confirm-logo-img"
                        />
                      </div>
                    ) : (
                      ''
                    )}
                  </div>
                </div>
              </Col>
              <Col xs={12}>
                <div className="confirm-field-item mb-0">
                  <div className="confirm-field-label">Location Address</div>
                  <div className="confirm-field-value">
                    {[
                      formik.values.address,
                      formik.values.city,
                      getStateName(formik.values.state_id),
                      getCountryName(formik.values.country_id),
                      formik.values.zipcode,
                    ].filter(Boolean).join(', ') || '-'}
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-between align-items-center">
          <Button
            variant="primary"
            type="button"
            className="btn-theme btn-border"
            onClick={() => setShowConfirmModal(false)}
            disabled={loading}
          >
            Back / Edit Details
          </Button>
          <Button
            variant="primary"
            type="button"
            className="btn-theme"
            onClick={handleConfirmSubmit}
            disabled={loading}
          >
            {loading && <Loader />}
            Confirm & Complete Setup <FontAwesomeIcon icon={faPlay} className="ms-1" />
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default StepLocation;